/* IBO custom (see theme/CUSTOMIZATIONS.md). Keeps every [data-ibo-fs] free-shipping
   message in sync with the cart, and keeps the header strip stuck under the sticky header.
   Listens to Canopy's cart events and also watches fetch() calls to /cart/add|change|
   update|clear (card steppers do not fire an event), then re-reads /cart.js.
   Also shows the "Added to cart" toast (#ibo-toast) after a successful /cart/add.
   2026-09-29: (1) cart page: re-renders the gap add-ons section ([data-ibo-gap-refresh], sections/ibo-cart-gap.liquid)
   after every cart change; the drawer copy is re-rendered by Canopy itself. (2) product page: shows the express
   checkout buttons ([data-ibo-express-min]) only while the selected variant costs at least that amount.
   2026-09-29: lines marked data-item-free (selected variant >= threshold) keep "This item ships FREE".
   2026-10-02 (checkout path): after_add_to_cart is "drawer", so Canopy opens the cart drawer (Checkout,
   express pay, free-shipping gap) after every add made through its product form (card "+", product page
   button, sticky bar, stock-up line). The toast is then skipped wherever a cart drawer exists; it still
   shows on pages without one (the cart page). An add made in Canopy's quick-buy drawer (multi-variant
   cards) closes that drawer and opens the cart drawer, which Canopy does not do by itself.
   2026-10-06 (Grand Opening $25+): a cart heavier than data-heavy-g grams (settings.ibo_heavy_cart_grams,
   setting 9071 with > = heavy at 9,072 g (20 lb) or more) gets state "heavy" (data-t-heavy text, bar hidden by CSS).
   2026-10-06 (QA fix-soon): lines with a variant (data-v-price, data-v-weight, set by Liquid and updated here on
   on:variant:change): variant heavier than data-heavy-g = state "vheavy" (data-t-vheavy); "This item ships FREE"
   only from data-if-min (threshold x 1.18 rounded up to $0.50); empty cart and a variant from the threshold up to
   data-if-min = data-t-band ("... before discount codes"). Same rules as snippets/ibo-free-shipping-line.liquid. */
(() => {
  const d = document;
  const SEL = '[data-ibo-fs]';
  // 2026-10-22 task 514 (CoS ruling, FTC free rule): a cart with a glass item is treated like a 20 lb+ cart
  // (neutral "Shipping calculated at checkout"). /cart.js has no tags, so the flag comes from
  // sections/ibo-cart-flags.liquid; first value from the Liquid render (data-cart-glass).
  let cartGlass = !!d.querySelector('[data-cart-glass="true"]');

  const num = (c, p, t, dc) => {
    const [a, b] = (c / 100).toFixed(p).split('.');
    return a.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (b ? dc + b : '');
  };
  const money = (c, fmt) => (fmt || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => ({
    amount: num(c, 2, ',', '.'),
    amount_no_decimals: num(c, 0, ',', '.'),
    amount_with_comma_separator: num(c, 2, '.', ','),
    amount_no_decimals_with_comma_separator: num(c, 0, '.', ','),
    amount_with_apostrophe_separator: num(c, 2, "'", '.'),
  }[k] || num(c, 2, ',', '.')));

  const render = (total, weight) => {
    d.querySelectorAll(SEL).forEach((el) => {
      const min = Number(el.dataset.min);
      if (!min) return;
      const left = Math.max(0, min - total);
      // 2026-10-06: variant rules (see the header comment)
      const ds = el.dataset;
      const heavyG = Number(ds.heavyG) || 0;
      const heavy = total > 0 && (cartGlass || (heavyG > 0 && Number(weight) > heavyG));
      const hasV = 'vPrice' in ds;
      const vP = Number(ds.vPrice), vW = Number(ds.vWeight), ifMin = Number(ds.ifMin) || 0;
      const vHeavy = !heavy && hasV && heavyG > 0 && vW > heavyG;
      const itemFree = !heavy && !vHeavy && hasV && ifMin > 0 && vP >= ifMin;
      const state = heavy ? 'heavy' : (vHeavy ? 'vheavy' : (itemFree ? 'free' : (total <= 0 ? 'empty' : (left > 0 ? 'away' : 'free'))));
      let tpl;
      if (itemFree) tpl = ds.tItem;
      else if (state === 'empty' && hasV && ds.tBand && vP >= min) tpl = ds.tBand;
      else tpl = ds[`t${state[0].toUpperCase()}${state.slice(1)}`];
      tpl = tpl || '';
      el.dataset.state = state;
      if ('hideEmpty' in el.dataset) el.hidden = state === 'empty';
      const text = el.querySelector('[data-ibo-fs-text]');
      if (text) text.innerHTML = tpl.replace('[[amount]]', `<strong>${money(left, el.dataset.fmt)}</strong>`);
      const bar = el.querySelector('[data-ibo-fs-bar]');
      if (bar) bar.style.setProperty('--ibo-p', `${itemFree ? 100 : Math.min(100, (total / min) * 100).toFixed(1)}%`);
    });
    // Product page shipping accordion line (snippets/ibo-pdp-accordions.liquid)
    // 2026-10-06 (forms + fixes): heavy variant, then item free (>= data-if-min), then band (>= data-min), else normal
    // 2026-10-07 (QA): a heavy cart comes first (same test as the main line's state "heavy"), text data-t-heavy
    d.querySelectorAll('[data-ibo-acc-ship]').forEach((el) => {
      const ds = el.dataset;
      const heavyG = Number(ds.heavyG) || 0;
      const vP = Number(ds.vPrice), ifMin = Number(ds.ifMin) || 0, min = Number(ds.min) || 0;
      let t;
      if (total > 0 && (cartGlass || (heavyG > 0 && Number(weight) > heavyG)) && 'tHeavy' in ds) t = ds.tHeavy; // 2026-10-22 task 514: neutral text
      else if (heavyG > 0 && Number(ds.vWeight) > heavyG) t = ds.tVheavy;
      else if (ifMin > 0 && vP >= ifMin && ds.tItem) t = ds.tItem;
      else if (min > 0 && vP >= min && ds.tBand) t = ds.tBand;
      else t = ds.tNormal;
      if (t != null) el.textContent = t;
    });
  };

  let timer;
  const refresh = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const due = toastDue; // only a read started after the add may reveal the toast
      const flags = fetch(`${root}?sections=ibo-cart-flags`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((j) => { const h = j['ibo-cart-flags'] || ''; if (/data-glass="(true|false)"/.test(h)) cartGlass = RegExp.$1 === 'true'; })
        .catch(() => {});
      Promise.all([fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()), flags])
        .then(([cart]) => render(cart.total_price, cart.total_weight))
        .catch(() => {})
        .then(refreshGap)
        .then(() => { placeToast(); if (due && toastDue) showToast(); });
    }, 50);
  };

  // "Added to cart" toast (2026-09-24). after_add_to_cart is "nothing" and the cart is no longer
  // docked, so a successful /cart/add shows a short, non-blocking confirmation instead of the cart.
  // Skipped while the cart drawer is open (the new line shows there) or Canopy's quick-buy drawer
  // is open (it shows its own "Item added to your cart." alert).
  // Shown after the /cart.js refresh has updated the header strip (fallback 1.5 s), so on the first
  // add to an empty cart it is placed below the strip that just appeared, not over it (QA 2026-09-24).
  let toast, live, hideT, toastDue = false, dueT, placeRaf;
  // Just below whatever header bars are on screen at the top (8 px gap), else 8 px from the top.
  // Re-measured while visible (strip or header size change, scroll, resize), so it never covers them.
  const placeToast = () => {
    if (!toast || toast.hidden) return;
    let top = 0;
    d.querySelectorAll('.cc-header, .ibo-fsb-section').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.height && r.bottom > top && r.bottom < window.innerHeight / 2) top = r.bottom;
    });
    toast.style.setProperty('--ibo-toast-top', `${Math.round(top) + 8}px`);
  };
  const queuePlace = () => {
    if (!toast || toast.hidden || placeRaf) return;
    placeRaf = requestAnimationFrame(() => { placeRaf = 0; placeToast(); });
  };
  const toastAfterRefresh = () => {
    toastDue = true;
    clearTimeout(dueT);
    dueT = setTimeout(() => { if (toastDue) showToast(); }, 1500);
  };
  const hideToast = () => {
    if (!toast) return;
    toast.classList.remove('is-visible');
    hideT = setTimeout(() => { toast.hidden = true; }, 250);
  };
  const scheduleHide = (ms) => { clearTimeout(hideT); hideT = setTimeout(hideToast, ms); };
  const initToast = () => {
    toast = d.getElementById('ibo-toast');
    live = d.getElementById('ibo-toast-live');
    if (!toast) return;
    d.body.appendChild(toast); // out of the sticky header, so position: fixed is the viewport
    if (live) d.body.appendChild(live);
    const link = toast.querySelector('[data-ibo-toast-view]');
    if (link) {
      link.addEventListener('click', (e) => {
        if (!d.querySelector('cart-drawer')) return; // cart page: follow the link
        e.preventDefault();
        hideToast();
        d.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: d.getElementById('cart-icon') } }));
      });
    }
    ['mouseenter', 'focusin'].forEach((ev) => toast.addEventListener(ev, () => clearTimeout(hideT)));
    ['mouseleave', 'focusout'].forEach((ev) => toast.addEventListener(ev, () => scheduleHide(1500)));
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(queuePlace);
      d.querySelectorAll('.cc-header, .ibo-fsb-section').forEach((el) => ro.observe(el));
    }
    window.addEventListener('scroll', queuePlace, { passive: true });
    window.addEventListener('resize', queuePlace);
  };
  // 2026-10-02: the cart drawer opens after an add (after_add_to_cart "drawer"), so no toast where one exists.
  const drawerOpensAfterAdd = () => {
    const s = window.theme && theme.settings;
    if (!s || !d.querySelector('cart-drawer')) return false;
    return s.afterAtc === 'drawer' || (s.afterAtc === 'drawer_desktop' && theme.mediaMatches && theme.mediaMatches.md);
  };
  const showToast = () => {
    toastDue = false;
    clearTimeout(dueT);
    if (!toast || drawerOpensAfterAdd() || d.querySelector('cart-drawer[open], quick-add-drawer[aria-hidden="false"]')) return;
    clearTimeout(hideT);
    toast.hidden = false;
    placeToast();
    void toast.offsetWidth; // restart the fade-in
    toast.classList.add('is-visible');
    if (live) {
      live.textContent = '';
      setTimeout(() => { live.textContent = toast.querySelector('.ibo-toast__msg').textContent.trim(); }, 50);
    }
    scheduleHide(3000);
  };

  // Cart page gap add-ons (2026-09-29): fetch this section again with the new cart and swap its content.
  const refreshGap = () => {
    d.querySelectorAll('[data-ibo-gap-refresh]').forEach((el) => {
      const id = el.dataset.iboGapRefresh;
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(id)}`;
      nativeFetch(url, { headers: { Accept: 'text/html' } })
        .then((r) => (r.ok ? r.text() : Promise.reject()))
        .then((html) => {
          const tpl = d.createElement('template');
          tpl.innerHTML = html;
          const fresh = tpl.content.querySelector(`[data-ibo-gap-refresh="${id}"]`);
          if (!fresh) return;
          el.innerHTML = fresh.innerHTML;
          if (typeof window.initLazyImages === 'function') window.initLazyImages();
        })
        .catch(() => {});
    });
  };

  // Product page express buttons (2026-09-29): hidden below data-ibo-express-min (cents) for the selected variant.
  d.addEventListener('on:variant:change', (e) => {
    const v = e.detail && e.detail.variant;
    // 2026-10-06: feed the new variant's price and weight to the shipping lines, then re-render
    const inQuick = e.target && e.target.closest && e.target.closest('quick-add-drawer');
    if (v && !inQuick && v.price != null && v.weight != null) {
      d.querySelectorAll('[data-ibo-fs][data-v-price], [data-ibo-acc-ship]').forEach((el) => {
        el.dataset.vPrice = v.price;
        el.dataset.vWeight = v.weight;
      });
      refresh();
    }
    const root = (e.target && e.target.closest && e.target.closest('.cc-main-product')) || d;
    root.querySelectorAll('[data-ibo-express-min]').forEach((el) => {
      el.hidden = !v || Number(v.price) < Number(el.dataset.iboExpressMin);
    });
  });

  // Stock-up line (2026-09-29, snippets/ibo-stock-up.liquid): set the main form's quantity, submit it through
  // Canopy's product-form (same path as Add to cart), then put the previous quantity back.
  d.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-ibo-stockup]');
    if (!btn) return;
    const form = d.getElementById(btn.dataset.form);
    const qty = form && form.querySelector('input[name="quantity"]');
    if (!form || !qty) return;
    e.preventDefault();
    const before = qty.value;
    qty.value = btn.dataset.iboStockup;
    qty.dispatchEvent(new Event('change', { bubbles: true }));
    if (typeof form.requestSubmit === 'function') form.requestSubmit(); else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    setTimeout(() => { qty.value = before; qty.dispatchEvent(new Event('change', { bubbles: true })); }, 1500);
  });

  const nativeFetch = window.fetch;
  window.fetch = function iboFetch(input, opts) {
    const p = nativeFetch.apply(this, arguments);
    try {
      const u = typeof input === 'string' ? input : (input && (input.url || input.href)) || '';
      if (/\/cart\/(add|change|update|clear)/.test(u)) p.then(refresh, refresh);
      if (/\/cart\/add/.test(u) && !/ibo_gift=1/.test(u)) p.then((r) => { if (r && r.ok) toastAfterRefresh(); }, () => {}); // task 238: no toast for the gift auto-add
    } catch (e) { /* ignore */ }
    return p;
  };
  ['on:cart:add', 'on:line-item:change', 'on:cart:error', 'on:cart-drawer:after-open'].forEach((e) => d.addEventListener(e, refresh));

  // 2026-10-02: an add inside Canopy's quick-buy drawer only shows "Item added" there (no checkout path).
  // Close it and open the cart drawer once Canopy has re-rendered the drawer contents (it waits 700 ms).
  ['on:cart:add', 'on:line-item:change'].forEach((ev) => d.addEventListener(ev, (e) => {
    const qa = e.target && e.target.closest && e.target.closest('quick-add-drawer');
    const cartDrawer = d.querySelector('cart-drawer');
    if (!qa || !cartDrawer || !drawerOpensAfterAdd()) return;
    setTimeout(() => {
      if (typeof qa.close === 'function') qa.close();
      setTimeout(() => { if (cartDrawer.getAttribute('open') === null) cartDrawer.open(); }, 250);
    }, 900);
  }));
  window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });

  // Stick the strip directly under Canopy's sticky header.
  const stick = () => {
    const bar = d.querySelector('.ibo-fsb-section');
    const header = d.querySelector('.cc-header');
    if (!bar || !header || !d.querySelector('store-header[data-is-sticky="true"]')) return;
    bar.classList.add('ibo-fsb--sticky');
    const set = () => d.documentElement.style.setProperty('--ibo-hh', `${header.getBoundingClientRect().height}px`);
    set();
    if ('ResizeObserver' in window) new ResizeObserver(set).observe(header);
  };
  const ready = () => { stick(); initToast(); };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', ready); else ready();
})();
